import { useEffect, useState } from 'react'
import { fetchMembers, selectMembers, setMemberTableCurrentPage, setFilters, setMembers } from '../../reducers/members'
import { useAppDispatch } from '../../reducers/store'
import DataGrid from '../data-grid/DataGrid'
import { memberColumns } from './MembersColumns'
import { useDispatch, useSelector } from 'react-redux'
import { Member, MemberConditionalAccessState, PaginationSelectOption } from '../../types/MemberTypes'
import DropDown from '../dropdown/DropDown'
import { activatedOptions } from '../../strings/dropdown-options'
import Popout from '../popout/Popout'
import {
  Button,
  Grid,
  IconButton,
  Popover,
  TextField,
  Text,
  Box,
  Card,
  Strong,
  SegmentedControl,
} from '@radix-ui/themes'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import AddMembersModal from './AddMembersModal'
import { CrossCircledIcon } from '@radix-ui/react-icons'
import AddCSVMembersModal from './AddCSVMembersModal'
import { useNavigate } from 'react-router-dom'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'
import { fetchConditionalAccessStatus } from '../../reducers/settings/settings.thunk.ts'
import { Pagination } from '../pagination/Pagination.tsx'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { IconFileAnalytics, IconUser } from '@tabler/icons-react'
import { inPov } from '../../utils/session.ts'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts.ts'

function MembersWrapper() {
  const { members, totalPages, memberTableCurrentPage, filters } = useSelector(selectMembers)
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [nameFilter, setNameFilter] = useState<string>('')
  const [showPopout, setShowPopout] = useState<boolean>(false)
  const [popoutData, setPopoutData] = useState<Member | null>(null)
  const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false)
  const [showCSVModal, setShowCSVModal] = useState<boolean>(false)
  const [openMemberOptions, setOpenMemberOptions] = useState<boolean>(false)
  const [showConditionalAccess, setShowConditionalAccess] = useState<boolean>(false)
  const [policies, setPolicies] = useState<string>('all')

  const { session } = useSelector(selectUser)

  useEffect(() => {
    di(fetchMembers(filters, memberTableCurrentPage))
    setNameFilter(filters.name)
    setPolicies(filters.policies)
  }, [filters])

  useEffect(() => {
    if (members || (members && (members as Member[]).length === 0)) {
      setDataLoading(false)
    } else if (!members) {
      setDataLoading(true)
    }
  }, [members])

  useEffect(() => {
    const session = localStorage.getItem('session') as string

    fetchConditionalAccess()

    if (session === '{}') {
      navigate('/login')
    }
    return () => {
      dispatch(setMembers(undefined))
    }
  }, [])

  const fetchConditionalAccess = async () => {
    let isActive = false
    const caSet = await di(fetchConditionalAccessStatus())
    if (caSet) {
      isActive = true
    }
    setShowConditionalAccess(isActive)
    if (isActive !== true) {
      dispatch(setFilters({ ...filters, policies: 'all' }))
    }
  }

  const clearNameFilter = () => {
    setDataLoading(true)
    setNameFilter('')
    dispatch(setFilters({ ...filters, name: '' }))
  }

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(fetchMembers({ ...filters }, data.selected))
  }

  const handleNoMembersText = () => {
    switch (policies) {
      case MemberConditionalAccessState.ALL:
        return 'No members found. Refine your search criteria.'
      case MemberConditionalAccessState.COMPLIANT:
        return 'No members are currently compliant based on your Conditional Access Policies or filters.'
      case MemberConditionalAccessState.BLOCKED:
        return 'No members are currently suspended based on your Conditional Access Policies or filters.'
      case MemberConditionalAccessState.FAILED:
        return 'No members have failed to be suspended by your Conditional Access Policies or filters.'
      default:
        return 'No members found. Refine your search criteria.'
    }
  }

  const resetFilters = () => {
    dispatch(setMemberTableCurrentPage(0))
    dispatch(setFilters({ name: '', activatedDevices: [], policies: '' }))
  }

  const updateNameFilter = () => {
    dispatch(setMemberTableCurrentPage(0))
    dispatch(setFilters({ ...filters, name: nameFilter }))
  }

  function updateActivatedDevicesFilter(value: string): void {
    dispatch(setMemberTableCurrentPage(0))
    dispatch(setFilters({ ...filters, activatedDevices: value }))
  }

  function updatePoliciesFilter(value: string): void {
    setPolicies(value)
    dispatch(setMemberTableCurrentPage(0))
    dispatch(setFilters({ ...filters, policies: value }))
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l w-[calc(100%_-_208px)] bg-contentBackground p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {dataLoading && (
          <div className="flex justify-center items-center min-h-screen">
            <PageSkeleton />
          </div>
        )}
        <div className="flex justify-between">
          <div className="mb-6 flex w-full">
            {showConditionalAccess && (
              <SegmentedControl.Root
                defaultValue={MemberConditionalAccessState.ALL}
                value={policies == '' ? MemberConditionalAccessState.ALL : policies}
                className="h-[38px] mr-4 bg-controllerBackground text-controllerText"
                onValueChange={updatePoliciesFilter}
                size={'2'}
              >
                <SegmentedControl.Item value={MemberConditionalAccessState.ALL}>All</SegmentedControl.Item>
                <SegmentedControl.Item value={MemberConditionalAccessState.COMPLIANT}>Compliant</SegmentedControl.Item>
                <SegmentedControl.Item value={MemberConditionalAccessState.BLOCKED}>Suspended</SegmentedControl.Item>
                <SegmentedControl.Item value={MemberConditionalAccessState.FAILED}>Failed</SegmentedControl.Item>
              </SegmentedControl.Root>
            )}
            <TextField.Root
              value={nameFilter}
              placeholder="Search..."
              className="w-[200px] h-[38px] mr-4 bg-inputBackground text-mainText dark:shadow-none border border-transparent dark:border-neutral-11 hover:shadow-none hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-mainBorder focus:ring-borderPurple p-0"
              onChange={(e) => {
                setNameFilter(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateNameFilter()
                }
              }}
            >
              <TextField.Slot className="p-0"></TextField.Slot>
              {nameFilter !== '' && (
                <TextField.Slot>
                  <IconButton
                    size="1"
                    variant="ghost"
                    onClick={() => {
                      clearNameFilter()
                    }}
                  >
                    <CrossCircledIcon height="14" width="14" className="cursor-pointer dark:text-white" />
                  </IconButton>
                </TextField.Slot>
              )}
              <TextField.Slot>
                <IconButton
                  size="1"
                  variant="ghost"
                  className="cursor-pointer dark:text-white"
                  onClick={updateNameFilter}
                >
                  <MagnifyingGlassIcon height="14" width="14" />
                </IconButton>
              </TextField.Slot>
            </TextField.Root>
            <DropDown
              value={filters?.activatedDevices}
              onChangeHandler={(value) => updateActivatedDevicesFilter(value)}
              options={activatedOptions}
              style="z-10 mr-4 min-w-[180px]"
              placeholder="Enrolled Devices"
              multiple={true}
              disabled={false}
            />
          </div>
          <div className="flex ml-auto justify-end">
            <Popover.Root open={openMemberOptions} onOpenChange={setOpenMemberOptions}>
              <Popover.Trigger>
                <Button className="h-[38px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple ml-8 cursor-pointer">
                  Add Members
                </Button>
              </Popover.Trigger>
              <Popover.Content width={'360px'} className="flex justify-content">
                <Grid columns={'2'} gapX={'10px'}>
                  <Box
                    maxWidth="160px"
                    className="cursor-pointer"
                    onClick={() => {
                      setOpenMemberOptions(false)
                      setShowCSVModal(true)
                    }}
                  >
                    <Card size="2">
                      <div
                        className="h-8 w-8 rounded flex justify-center items-center"
                        style={{ backgroundColor: '#F2F0FF' }}
                      >
                        <IconFileAnalytics size={20} color="#6128D1" />
                      </div>
                      <Text as="p" size="1" className="mt-5">
                        <Strong>CSV</Strong>
                        <br /> Upload a populated CSV file for bulk import.
                      </Text>
                    </Card>
                  </Box>
                  <Box
                    maxWidth="160px"
                    className="cursor-pointer"
                    onClick={() => {
                      setOpenMemberOptions(false)
                      setShowAddMemberModal(true)
                    }}
                  >
                    <Card size="2">
                      <div
                        className="h-8 w-8 rounded flex justify-center items-center"
                        style={{ backgroundColor: '#F2F0FF' }}
                      >
                        <IconUser size={20} color="#6128D1" />
                      </div>
                      <Text as="p" size="1" className="mt-5">
                        <Strong>Manual</Strong>
                        <br /> Enter members manually using their emails.
                      </Text>
                    </Card>
                  </Box>
                </Grid>
              </Popover.Content>
            </Popover.Root>
          </div>
        </div>

        {members && members.length > 0 && (
          <>
            <DataGrid
              columns={memberColumns(setShowPopout, setPopoutData, showConditionalAccess)}
              data={members}
              loading={dataLoading}
              classNames={inPov(session) ? 'max-h-[calc(100vh_-_276px)]' : 'max-h-[calc(100vh_-_240px)]'}
            />
            <div className="mt-2">
              <Pagination
                totalPages={totalPages}
                forcePage={memberTableCurrentPage}
                handlePageClick={handlePageClick}
              />
            </div>
          </>
        )}
        {members && members.length === 0 && (
          <div className="block items-center mt-20">
            <div className="flex items-center justify-center">
              <MagnifyingGlassIcon className="h-12 w-12" />
            </div>
            <p className="text-center font-bold">No Results Found</p>
            <p className="text-center text-sm flex mx-auto max-w-[300px]">{handleNoMembersText()}</p>
            <Button
              className="mt-4 text-center flex bg-black mx-auto outline-1 dark:outline dark:outline-white"
              onClick={() => {
                resetFilters()
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>
      {showPopout && <Popout setShowPopout={setShowPopout} contentScreen="MEMBERS" popoutData={popoutData} />}
      {showAddMemberModal && <AddMembersModal setShowModal={setShowAddMemberModal} />}
      {showCSVModal && <AddCSVMembersModal setShowCSVModal={setShowCSVModal} />}
    </>
  )
}

export default MembersWrapper
